import 'leaflet-draw';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren, ReactElement, useEffect, useRef } from 'react';
import { useMap } from '../../../../useMap';
import { ISegmentation } from '../annotations.interface';
import { SegmentationFrameDrawer } from './DrawControl.models/SegmentationFrameDrawer';

interface IProps extends PropsWithChildren {
  readonly: boolean;
  editable: boolean;
  segmentation?: ISegmentation;
  onSegmentationSelected: (id: string) => void;
  onSegmentationDeselected: () => Promise<void>;
  onNextSegmentationSelected: () => ISegmentation | undefined;
  onPreviousSegmentationSelected: () => ISegmentation | undefined;
}

export const SegmentationFrameDrawControl = observer((props: IProps): ReactElement => {
  const map = useMap();
  const segmentationFrameDrawerRef = useRef<SegmentationFrameDrawer | null>(null);

  useEffect(() => {
    map.keyboard.disable();
    setSegmentationFrameDrawer(props);

    return (): void => {
      if (segmentationFrameDrawerRef.current) {
        segmentationFrameDrawerRef.current.clearFrame();
        segmentationFrameDrawerRef.current.removeHandlers();
      }
    };
  }, []);

  useEffect(() => {
    if (segmentationFrameDrawerRef.current) {
      segmentationFrameDrawerRef.current.update({
        readonly: props.readonly,
        editable: props.editable,
        segmentation: props.segmentation,
        selectSegmentation: props.onSegmentationSelected,
        nextSegmentationSelected: props.onNextSegmentationSelected,
        previousSegmentationSelected: props.onPreviousSegmentationSelected,
        segmentationDeselected: props.onSegmentationDeselected,
      });
    }
  }, [
    props.readonly,
    props.editable,
    props.segmentation,
    props.onSegmentationSelected,
    props.onNextSegmentationSelected,
    props.onPreviousSegmentationSelected,
    props.onSegmentationDeselected,
  ]);

  const setSegmentationFrameDrawer = (props: IProps): void => {
    segmentationFrameDrawerRef.current = new SegmentationFrameDrawer(map, {
      readonly: props.readonly,
      editable: props.editable,
      segmentation: props.segmentation,
      selectSegmentation: props.onSegmentationSelected,
      nextSegmentationSelected: props.onNextSegmentationSelected,
      previousSegmentationSelected: props.onPreviousSegmentationSelected,
      segmentationDeselected: props.onSegmentationDeselected,
    });

    segmentationFrameDrawerRef.current.addHandlers();
  };

  return <>{props.children}</>;
});
